import { DsButton } from "../../../design-system";
/** Shared wizard step validators used across policy components. */

export const ORG_APPLICABILITY_KEYS = [
  "branch",
  "department",
  "designation",
  "grade",
  "category",
  "subDepartment",
  "location",
];

export function validateBasicsStep(policy) {
  if (!String(policy.policyName || policy.name || "").trim()) {
    return "Policy name is required before continuing.";
  }
  if (!String(policy.effectiveDate || "").trim()) {
    return "Effective date is required before continuing.";
  }
  return null;
}

export function validateWorkflowStep(policy, { letterField = "letterFormat" } = {}) {
  if (
    policy.approvalMethod === "workflow" &&
    (!policy.workflowSteps || policy.workflowSteps.length === 0)
  ) {
    return "Add at least one workflow step for 'Through workflow'.";
  }
  return null;
}

export function validateLettersStep(policy, options = {}) {
  const {
    letterField = "letterFormat",
    cancellationField = "cancellationLetterFormat",
    hasCancellation = false,
  } = options;
  if (policy.autoLetter === "yes" && !String(policy[letterField] || "").trim()) {
    return "Choose a letter template when auto letter is enabled.";
  }
  if (hasCancellation && policy.useCancellationLetter && !String(policy[cancellationField] || "").trim()) {
    return "Choose a cancellation letter template.";
  }
  return null;
}

export function validateChecklistStep(policy) {
  const incomplete = (policy.checklistMappings || []).find(
    (item) => !String(item.name || "").trim()
  );
  if (incomplete) {
    return "Each checklist item needs a name.";
  }
  return null;
}

export function validateApplicabilityStep(policy) {
  const hasScope =
    !!policy.allEmployees ||
    ORG_APPLICABILITY_KEYS.some((k) => policy[k]) ||
    String(policy.individualEmployeeIds || "").trim();
  if (!hasScope) {
    return "Select applicability scope (all employees, org filters, or individual employee IDs).";
  }
  return null;
}

export function createPolicyWizardNav({
  policyFormStep,
  steps,
  validateStep,
  setPolicyFormStep,
  setPolicySaveError,
  onCancel,
  onPreviewSave,
  onFlowchartPreview,
  darkMode,
}) {
  const goToNextPolicyStep = () => {
    const err = validateStep(policyFormStep);
    if (err) {
      setPolicySaveError(err);
      return;
    }
    setPolicySaveError(null);
    if (policyFormStep < steps.length) {
      setPolicyFormStep((s) => s + 1);
    }
  };

  const goToPreviousPolicyStep = () => {
    setPolicySaveError(null);
    if (policyFormStep > 1) {
      setPolicyFormStep((s) => s - 1);
    }
  };

  const handleStepClick = (stepId) => {
    if (stepId < policyFormStep) {
      setPolicySaveError(null);
      setPolicyFormStep(stepId);
    }
  };

  const footer = (
    <>
      {policyFormStep === steps.length && onFlowchartPreview && (
        <DsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={onFlowchartPreview}
          title="Open preview; use Print / Save as PDF inside the preview"
        >
          Preview Flowchart
        </DsButton>
      )}
      <DsButton
        type="button"
        variant="outline"
        size="sm"
        onClick={policyFormStep === 1 ? onCancel : goToPreviousPolicyStep}
      >
        {policyFormStep === 1 ? "Cancel" : "Previous"}
      </DsButton>
      {policyFormStep < steps.length ? (
        <DsButton type="button" variant="primary" size="sm" onClick={goToNextPolicyStep}>
          Next: {steps[policyFormStep]?.label}
        </DsButton>
      ) : (
        <DsButton type="button" variant="primary" size="sm" onClick={onPreviewSave}>
          Preview &amp; Save Policy
        </DsButton>
      )}
    </>
  );

  return { goToNextPolicyStep, goToPreviousPolicyStep, handleStepClick, footer };
}
